// src/views/events/edit/VShowEvent.tsx
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CLayout } from '../../../components';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Breadcrumbs,
    Card,
    Divider,
    Snackbar,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    Typography,
} from '@mui/joy';
import { ChevronRight, AccessTime, LocationOn, AttachMoney } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import AutoAwesome from '@mui/icons-material/AutoAwesome'; // Import ajouté pour l'icône de boost
import { styled } from '@mui/system';
import { get } from '../../../api/api';
import { EventFormProvider } from '../../../context/EventFormContext';
import EventForm from '../../../components/EventForm';
import { EventStatistics } from '../../../components/EventStatistics';
import EventBoost from '../../../components/EventBoost';
import { getUser } from '../../../api/auth';

const StyledCard = styled(Card)(({ theme }) => ({
    margin: theme.spacing(5),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
    backgroundColor: '#f9f9f9',
}));

const SmallCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: '#fff',
}));

const getLinkHref = (link: string): string => {
    if (!link) return '#';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9\s\-().]{7,}$/;

    if (emailRegex.test(link)) {
        return `mailto:${link}`;
    } else if (phoneRegex.test(link)) {
        return `tel:${link.replace(/\s+/g, '')}`;
    } else {
        return link.startsWith('http') ? link : `https://${link}`;
    }
};


// Composant pour afficher les détails d'un évènement
const VShowEventView = ({ event }: { event: any }) => (
    <StyledCard variant="outlined">
        {event.urls_image && event.urls_image.length > 0 && (
            <img
                src={event.urls_image[0]}
                alt={event.name}
                style={{
                    width: '100%',
                    height: 300,
                    objectFit: 'cover',
                    borderRadius: 8,
                    marginBottom: 16,
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                }}
            />
        )}
        <Typography level="h2" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
            {event.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AccessTime sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Date de début :</strong> {new Date(event.start_date).toLocaleString()}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AccessTime sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Date de fin :</strong> {new Date(event.end_date).toLocaleString()}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationOn sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Lieu :</strong> {event.location_name}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationOn sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Adresse :</strong> {event.address} {event.postal_code}, {event.city}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AttachMoney sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Prix :</strong> {event.price} €
            </Typography>
        </Box>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LinkIcon sx={{ mr: 1, color: '#777' }} />
            <Typography level="body-sm">
                <strong>Billetterie :</strong>{' '}
                <a href={event.ticketing_link} target="_blank" rel="noopener noreferrer">
                    {event.ticketing_link}
                </a>
            </Typography>
        </Box> */}

        {event.ticketing_link && (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LinkIcon sx={{ mr: 1, color: '#777' }} />
        <Typography level="body-sm">
            <strong>Billetterie :</strong>{' '}
            <a href={getLinkHref(event.ticketing_link)} target="_blank" rel="noopener noreferrer">
                {event.ticketing_link}
            </a>
        </Typography>
    </Box>
)}


        {/* Nouveau champ boosté */}
        {event.boosted !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AutoAwesome sx={{ mr: 1, color: '#777' }} />
                <Typography level="body-sm">
                    <strong>Boosté :</strong> {event.boosted ? 'Oui' : 'Non'}
                </Typography>
            </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <SmallCard variant="outlined" sx={{ marginRight: '10px' }}>
                <Typography level="body-sm">
                    <strong>Organisateur :</strong> {event.organizer_name}
                </Typography>
            </SmallCard>
            <SmallCard variant="outlined" sx={{ marginRight: '10px' }}>
                <Typography level="body-sm">
                    <strong>Artistes :</strong> {event.artists && event.artists.join(', ')}
                </Typography>
            </SmallCard>
            <SmallCard variant="outlined">
                <Typography level="body-sm">
                    <strong>Catégories :</strong>{' '}
                    {event.sub_categories &&
                        event.sub_categories
                            .map((sub: any) => `${sub.category.name} (${sub.name})`)
                            .join(', ')}
                </Typography>
            </SmallCard>
        </Box>

        <Typography level="body-md" sx={{ mt: 2, fontWeight: 'bold', color: '#333' }}>
            Description :
        </Typography>
        <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap', color: '#555' }}>
            {event.description}
        </Typography>
    </StyledCard>
);

// Composant affichant les statistiques et prévisions de l'évènement
export const VShowEvent = (): React.ReactElement => {
    const user = getUser();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const eventId = searchParams.get('id') || 0;

    const [snackbar, setSnackbar] = useState({
        open: false,
        color: 'success' as 'danger' | 'primary' | 'neutral' | 'success' | 'warning' | undefined,
        message: '',
    });
    const [event, setEvent] = useState<any>({});

    // Charger l'évènement pour la vue détaillée
    useEffect(() => {
        get({ url: `events/${eventId}` })
            .then((res) => {
                setEvent(res.data);
            })
            .catch((err) => console.log(err));
    }, [eventId]);

    return (
        <CLayout>
            <section id="VShowEvent">
                <Card className="rounded-none my-4 drop-shadow-lg" variant="outlined">
                    <div className="flex items-center gap-4 justify-between w-full">
                        <Breadcrumbs separator={<ChevronRight />} className="!p-0">
                            <Link to="/">{t('EevenPRO')}</Link>
                            <Link to="/events">{t('Liste des évènements')}</Link>
                            <Typography level="inherit">{t("Détails d'un évènement")}</Typography>
                        </Breadcrumbs>
                    </div>
                </Card>

                <Tabs defaultValue={0}>
                    <TabList>
                        <Tab value={0}>{t("Voir l'évènement")}</Tab>
                        <Tab value={1}>{t("Modifier l'évènement")}</Tab>
                        <Tab value={2}>{t("Statistiques")}</Tab>
                        {user?.roles?.includes('ROLE_ADMIN') && (
                            <Tab value={3}>{t("Boost")}</Tab>
                        )}
                    </TabList>

                    {/* Onglet Vue détaillée */}
                    <TabPanel value={0} className="-m-[2rem]">
                        <VShowEventView event={event} />
                    </TabPanel>

                    {/* Onglet Édition */}
                    <TabPanel value={1} className="mt-[.3rem]">
                        <EventFormProvider>
                            {eventId && <EventForm mode="edit" eventId={eventId} />}
                        </EventFormProvider>
                    </TabPanel>

                    {/* Onglet Statistiques */}
                    <TabPanel value={2} className="-mx-[.5rem] mt-[.3rem]">
                        <EventStatistics event={event} />
                    </TabPanel>

                    {/* Onglet Boost */}
                    {user?.roles?.includes('ROLE_ADMIN') && (
                        <TabPanel value={3} className="-mx-[.5rem] mt-[.3rem]">
                            <EventBoost event={event} />
                        </TabPanel>
                    )}
                </Tabs>

                <Snackbar
                    open={snackbar.open}
                    variant="soft"
                    color={snackbar.color}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Snackbar>
            </section>
        </CLayout>
    );
};